/**
 * «Record / Deal / Whole base» (§8.0, subject model R1 and R8): a button
 * CHOOSES the level, and the subject is the screen's projection onto it. What
 * is unavailable is disabled with the reason written out under the buttons —
 * never hidden, and never only in a `title`: a disabled button takes no
 * pointer events, so a tooltip on it is never seen, and the Bitrix24 iframe
 * has no hover at all (#1041 review). A contour that is simply not out yet
 * stays a quiet «coming soon» tooltip on the wrapper, not a line of text.
 *
 * - record: needs a subject whose access lets it ask about one (a grant
 *   without the record or its text, #1677 — said first, on any screen: it
 *   does not depend on the screen), then a record on screen;
 * - deal (lead, contact, company): needs the contour released AND what the
 *   record needs AND a record linked to one; a topic already about a deal
 *   keeps its button pressed even when the screen moved off it;
 * - whole base: needs the contour released (Ф3) and a subject whose access
 *   does not refuse it (a grant holder, #1645 — said on the reason line, not
 *   as «coming soon»); it never depends on the screen, so otherwise only a
 *   question in flight stops it.
 *
 * The reason line describes the switcher as a group (`role="group"`), not
 * each button: it can speak of one level while another is the focused one,
 * and a disabled button is not focusable to hear it anyway — a screen reader
 * entering the group hears why, as a sighted user reads it (Codex P2, #1709).
 *
 * No switching while a paid turn is in flight — the drawer's `busy`, the one
 * decider every entry point defers to (R6). The unsent question moves with a
 * switch: the level changed, the question did not (spec §10.16).
 *
 * Rendered by the drawer OUTSIDE the panel: the panel is keyed by the subject
 * and remounts on every switch, which would drop keyboard focus from the very
 * button that was pressed (#1041 review).
 *
 * In the web app «whole base» is a page, not a level of the drawer: only the
 * page has the filters (#1357). The host that has that page passes
 * `onOpenWholeBase`; the page opens and closes the drawer on arrival — a
 * navigation kept back by unsaved settings leaves the drawer as it was
 * (Codex P2, #1374). Already on the page, the drawer closes here. The unsent
 * question stays in the drawer, under the record it was typed for — carried
 * over, it raced the page's own history, filters and draft (code-review on
 * #1374). Inside Bitrix24 there is no such page, and the button switches the
 * drawer as before.
 */

import { useId } from 'react';
import { useTranslation } from 'react-i18next';
import { isAssistantScopeReleased } from '@aica/api-types/schemas';
import {
  useAssistantDrawerSafe,
  type AssistantTopic,
} from '@/contexts/AssistantDrawerContext';
import { Button } from '@/components/ui/button';
import { useOrgTopicState } from '@/hooks/assistant/useOrgTopic';
import { useAssistantSessionSafe } from './AssistantSession';

export interface ScopeSwitcherProps {
  /**
   * Opens the whole-base page; `true` — it is already open (no arrival will
   * close the drawer). Absent — switch the drawer.
   */
  onOpenWholeBase?: () => boolean;
}

export function ScopeSwitcher({ onOpenWholeBase }: ScopeSwitcherProps = {}) {
  const { t } = useTranslation('assistant');
  const drawer = useAssistantDrawerSafe();
  const session = useAssistantSessionSafe();
  const { topic: orgTopic, refused: orgRefused } = useOrgTopicState();
  const reasonId = useId();
  const labelId = useId();
  if (!drawer) return null;
  const {
    topic,
    screenTopic,
    screenEntityTopic,
    recordRefused,
    busy,
    openAssistant,
    closeAssistant,
  } = drawer;
  const scope = topic?.subject.scope ?? 'record';
  const entityReleased = isAssistantScopeReleased('entity');
  // The pressed button names the topic the conversation IS about. A topic
  // frozen by a question in flight can differ from the screen — a deal on
  // the panel, a contact's record behind it — and «This contact» over a deal's
  // conversation would misname it (Codex P2, #1041). The screen names the
  // button only while it is not the current level.
  const entityType =
    topic?.subject.scope === 'entity'
      ? topic.subject.entityType
      : (screenEntityTopic?.subject.entityType ?? null);

  // An access that refuses records is said before «open a record»: it holds
  // on every screen, and opening one would not help (#1677, review #1709).
  // The provider projects no record for it, so `screenTopic` is `null` then.
  const recordReason = busy
    ? t('scope.busy')
    : recordRefused
      ? t('scope.recordRefused')
      : !screenTopic
        ? t('scope.noRecord')
        : null;
  // A deal needs what a record needs, then a deal behind it. No record on
  // screen is not a record without a deal: the dashboard behind a retained
  // panel must not be called «this record» (Codex P2, #1041).
  const entityReason = recordReason ?? (!screenEntityTopic ? t('scope.noEntity') : null);

  const choose = (next: AssistantTopic) => {
    session?.carryDraftTo(next.subject);
    openAssistant(next);
  };

  // The reason the user needs to see: the level they cannot switch to. From
  // the whole base both narrower levels need a record — one reason for both
  // while there is none; with a record on screen but no CRM entity behind it,
  // the record button works and the entity one says why it does not (Codex
  // P3, #1312).
  // A topic the access refuses (a turn held readable, #1677) says so whatever
  // its level: the composer under it is closed for that reason.
  const shownReason =
    scope === 'org'
      ? (recordReason ?? (entityReleased ? entityReason : null))
      : recordRefused
        ? t('scope.recordRefused')
        : scope === 'record'
          ? entityReleased
            ? entityReason
            : null
          : recordReason;
  // The whole base refused by the subject's access is not «coming soon»: it
  // is said on the same line, beside the reason for the other levels (review
  // #1678).
  const orgReason = orgRefused && scope !== 'org' ? t('scope.orgRefused') : null;
  const reasonLine = [shownReason, orgReason].filter(Boolean).join(' ');
  const describedBy = reasonLine ? reasonId : undefined;
  const orgButton = (
    <Button
      variant={scope === 'org' ? 'primary' : 'outline'}
      size="sm"
      aria-pressed={scope === 'org'}
      disabled={scope !== 'org' && (!orgTopic || busy)}
      onClick={() => {
        if (scope === 'org' || !orgTopic) return;
        if (onOpenWholeBase) {
          if (onOpenWholeBase()) closeAssistant();
          return;
        }
        choose(orgTopic);
      }}
      data-testid="assistant-scope-org"
    >
      {t('scope.org')}
    </Button>
  );

  const entityButton = (
    <Button
      variant={scope === 'entity' ? 'primary' : 'outline'}
      size="sm"
      aria-pressed={scope === 'entity'}
      disabled={scope !== 'entity' && (!entityReleased || entityReason !== null)}
      onClick={() => {
        if (scope !== 'entity' && screenEntityTopic) choose(screenEntityTopic);
      }}
      data-testid="assistant-scope-entity"
    >
      {entityType ? t(`scope.entityOf.${entityType}`) : t('scope.entity')}
    </Button>
  );

  return (
    <div className="flex flex-col gap-1">
      <div
        role="group"
        aria-labelledby={labelId}
        aria-describedby={describedBy}
        className="flex flex-wrap items-center gap-2"
        data-testid="assistant-scope-group"
      >
        <span id={labelId} className="text-xs text-fg-muted">
          {t('scope.label')}
        </span>
        <Button
          variant={scope === 'record' ? 'primary' : 'outline'}
          size="sm"
          aria-pressed={scope === 'record'}
          disabled={scope !== 'record' && recordReason !== null}
          onClick={() => {
            if (scope !== 'record' && screenTopic) choose(screenTopic);
          }}
          data-testid="assistant-scope-record"
        >
          {t('scope.record')}
        </Button>
        {/* A contour that is not out yet: the tooltip sits on a wrapper — a
            disabled button takes no pointer events, so its own `title` is
            never shown. */}
        {entityReleased ? (
          entityButton
        ) : (
          <span title={t('scope.notYet')} className="inline-flex">
            {entityButton}
          </span>
        )}
        {orgTopic || orgRefused ? (
          orgButton
        ) : (
          <span title={t('scope.notYet')} className="inline-flex">
            {orgButton}
          </span>
        )}
      </div>
      {reasonLine && (
        <p
          id={reasonId}
          className="text-xs text-fg-subtle"
          data-testid="assistant-scope-reason"
        >
          {reasonLine}
        </p>
      )}
    </div>
  );
}
